// The unlocked per-clinic key set used by repositories.
import { decryptFields, encryptFields, importAesKey, type ClinicKey } from './envelope';
import { blindIndex, deriveIndexKey } from './blind-index';
import { storeClinicKeys } from './keystore';

export class ClinicCrypto {
  private constructor(
    readonly clinicId: string,
    readonly active: ClinicKey,
    private readonly all: Map<number, CryptoKey>,
    private readonly indexKey: CryptoKey,
    /** Raw active key, held in memory only while this unlock lasts; needed to wrap for colleagues. */
    private raw: Uint8Array | null,
  ) {}

  /** From a raw 32-byte key (fresh clinic, or just unwrapped from a grant). Persists a device copy. */
  static async fromRaw(clinicId: string, version: number, raw: Uint8Array): Promise<ClinicCrypto> {
    const aes = await importAesKey(raw);
    const index = await deriveIndexKey(raw);
    await storeClinicKeys(clinicId, version, aes, index);
    return new ClinicCrypto(clinicId, { version, key: aes }, new Map([[version, aes]]), index, raw);
  }

  /** From keys already on this device. */
  static fromStored(clinicId: string, stored: { version: number; aes: CryptoKey; index: CryptoKey }[]): ClinicCrypto | null {
    if (stored.length === 0) return null;
    const newest = stored.reduce((a, b) => (b.version > a.version ? b : a));
    return new ClinicCrypto(clinicId, { version: newest.version, key: newest.aes }, new Map(stored.map((s) => [s.version, s.aes])), newest.index, null);
  }

  /** Available only in the sign-in that unwrapped the key (not after a restore from the device keystore). */
  exportRawForWrap(): Uint8Array {
    if (!this.raw) throw new Error('raw key not available on this device session');
    return this.raw;
  }
  get canGrant(): boolean { return this.raw !== null; }

  encrypt(rowId: string, fields: Record<string, unknown>) { return encryptFields(this.active, rowId, fields); }
  decrypt<T = Record<string, unknown>>(rowId: string, enc: string) { return decryptFields<T>(this.all, rowId, enc); }
  index(kind: 'phone' | 'nrc' | 'name', canonical: string) { return blindIndex(this.indexKey, kind, canonical); }
}
